import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import Select from 'react-select';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { Activity, Download, Brain, RefreshCw, ChevronDown, Calendar, Settings, Edit2, LogOut, Lock, User, Mail, HelpCircle, X } from 'lucide-react';
import { format } from 'date-fns';

import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

function App() {
  // --- State定義 ---
  const [config, setConfig] = useState(null);
  
  const [user, setUser] = useState(null);
  const [usernameInput, setUsernameInput] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [deviceAliases, setDeviceAliases] = useState({});
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const [isHelpOpen, setIsHelpOpen] = useState(false);

  // ★AI診断用のState
  const [isDiagnosisOpen, setIsDiagnosisOpen] = useState(false);
  const [diagnosisResult, setDiagnosisResult] = useState(null);
  const [isDiagnosing, setIsDiagnosing] = useState(false);

  const [fileList, setFileList] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [allData, setAllData] = useState({});
  const [loading, setLoading] = useState(false);
  
  // 初期値を今日の日付にする
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
  });
  
  const [endDate, setEndDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  });

  const [timeRange, setTimeRange] = useState([0, 1439]);
  const [displayMode, setDisplayMode] = useState('overlap');
  const [targetMetric, setTargetMetric] = useState('both');
  
  const [isTimeMenuOpen, setIsTimeMenuOpen] = useState(false);
  const [filterMode, setFilterMode] = useState('quick');
  const [quickLabel, setQuickLabel] = useState('Today');

  // ★追加: APIに送る期間パラメータ（初期値: today）
  const [apiDuration, setApiDuration] = useState('today');

  // --- AI診断用の追加State ---
  const [isAiSelectOpen, setIsAiSelectOpen] = useState(false); // 選択画面の開閉
  const [aiLoading, setAiLoading] = useState(false);           // ロード中の表示
  const aiAbortController = useRef(null);                      // キャンセル制御用

  const quickRanges = [
    { label: 'Today', value: 'today' },
    { label: 'Last 15 minutes', value: 15 },
    { label: 'Last 1 hour', value: 60 },
    { label: 'Last 6 hours', value: 360 }, // "hour" -> "hours" に修正
    { label: 'Last 12 hours', value: 720 },
    { label: 'Last 24 hours', value: 1440 },
    { label: 'Last 3 days', value: 4320 },
    { label: 'Last 7 days', value: 10080 },
  ];

  // 1. 設定ファイル読み込み
  useEffect(() => {
    fetch('/settings.json')
      .then(res => res.json())
      .then(data => {
        setConfig(data);
        const savedAliases = JSON.parse(localStorage.getItem('deviceAliases') || '{}');
        if (data.deviceNames) {
           setDeviceAliases({ ...data.deviceNames, ...savedAliases });
        } else {
           setDeviceAliases(savedAliases);
        }
      })
      .catch(err => console.error("Config load error:", err));
  }, []);

  // 2. ファイルリスト取得 (全選択対応版)
  useEffect(() => {
    if (!user || !config) return;

    fetch(`${config.apiBase}/api/list`)
      .then(res => res.json())
      .then(data => {
        // data は [{ id: "...", name: "..." }] の形

        // 1. 権限チェック (フィルタリング)
        const allowedFiles = data.filter(item => {
          const userData = config.users[user];
          const perms = userData ? userData.permissions : [];
          if (perms.includes("*")) return true;
          // item.id でチェック
          return perms.some(p => item.id.includes(p));
        });

        // 2. react-select用にデータを整形
        const options = allowedFiles.map(item => {
          return {
            value: item.id,                
            label: item.name || item.id,   // 名前があれば名前、なければID
            rawName: item.id
          };
        });

        // 3. 選択肢リストを更新
        setFileList(options);

        // ★ここが重要: リストがあるなら、即座に「全選択」状態にする
        if (options.length > 0) {
          setSelectedDevices(options);
        }
      })
      .catch(e => console.error("List fetch error:", e));
  }, [user, config]);

  // 3. データ取得 (★D1対応版に変更)
  const fetchData = async () => {
    if (!config || selectedDevices.length === 0) return;
    setLoading(true);
    const newData = {};
    
    // 選択されたデバイスごとにAPIを叩く
    const promises = selectedDevices.map(async (device) => {
      try {
        // ★変更: rawNameとdurationを使ってD1 APIからJSONを取得
        const url = `${config.apiBase}/api/data?device=${encodeURIComponent(device.rawName)}&duration=${apiDuration}`;
        const res = await fetch(url);
        const json = await res.json();
        
        // D1からのデータ形式: [[timestamp, 0, ping, loss, down, up], ...]
        newData[device.label] = json;
      } catch (e) {
        console.error(`Error fetching ${device.label}:`, e);
      }
    });

    await Promise.all(promises);
    setAllData(newData);
    setLoading(false);
  };

  // 期間やデバイス、ユーザーが変わったら再取得
  useEffect(() => { 
    if(user && config) fetchData(); 
    // 次の行で警告を無視する設定を追加
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevices, user, config, apiDuration]);

  // 名前変更処理 (D1へ保存)
  const handleRename = async (rawName, newName) => {
    if (!config) return;

    try {
      // 1. サーバーに新しい名前を送信
      await fetch(`${config.apiBase}/api/update-name`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          device_id: rawName,
          name: newName
        })
      });

      // 2. ローカルの表示も一時的に更新 (リロードしなくても画面が変わるように)
      setFileList(prev => prev.map(item => {
        if (item.value === rawName) {
          return { ...item, label: newName };
        }
        return item;
      }));
      
      // 必要であればここで再度リストを取得しても良い
      // alert("名前を保存しました");

    } catch (e) {
      console.error("Rename error:", e);
      alert("名前の保存に失敗しました");
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (!config) return;
    const userData = config.users[usernameInput];
    if (userData && userData.password === passwordInput) {
      setUser(usernameInput);
    } else {
      alert("ユーザー名またはパスワードが間違っています");
    }
  };

  // グラフデータ計算
  const filteredChartData = useMemo(() => {
    const datasets = [];
    // D1のデータ構造に合わせる: [0]:Timestamp, [2]:Ping, [3]:Loss, [4]:Down, [5]:Up
    // ※元のCSV: [3]:Loss, [4]:Down... だったがD1 API側で合わせてある前提
    // APIレスポンス: [timestamp, 0, ping, loss, down, up]
    let valueIndex = 4; // default download
    if (targetMetric === 'upload') valueIndex = 5;
    if (targetMetric === 'ping') valueIndex = 2;

    Object.entries(allData).forEach(([deviceName, rows], index) => {
      const filteredRows = rows.filter(r => {
        if (!r[0]) return false;
        const rowTime = r[0]; // timestamp (number)
        const rowDate = new Date(rowTime);
        
        if (filterMode === 'quick') {
          // Quickモードなら、API側ですでに期間フィルタ済みだが、念のためグラフ表示範囲でクリップ
          return rowDate >= startDate && rowDate <= endDate;
        } else {
          // Manualモード
          const compareDate = new Date(rowDate.getFullYear(), rowDate.getMonth(), rowDate.getDate());
          const s = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
          const e = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
          const rowMin = rowDate.getHours() * 60 + rowDate.getMinutes();
          return compareDate >= s && compareDate <= e && rowMin >= timeRange[0] && rowMin <= timeRange[1];
        }
      });

      if (targetMetric === 'both') {
        datasets.push({
          label: `${deviceName} (Down)`,
          data: filteredRows.map(r => ({ x: r[0], y: Number(r[4]) })),
          borderColor: `hsl(${index * 140}, 70%, 50%)`,
          tension: 0.3, pointRadius: 2, borderWidth: 2
        });
        datasets.push({
          label: `${deviceName} (Up)`,
          data: filteredRows.map(r => ({ x: r[0], y: Number(r[5]) })),
          borderColor: `hsl(${index * 140}, 70%, 50%)`,
          borderDash: [5, 5], tension: 0.3, pointRadius: 2, borderWidth: 2
        });
      } else {
        datasets.push({
          label: `${deviceName} (${targetMetric === 'download' ? 'Down' : targetMetric === 'upload' ? 'Up' : 'Ping'})`,
          data: filteredRows.map(r => ({ x: r[0], y: Number(r[valueIndex]) })),
          borderColor: `hsl(${index * 140}, 70%, 50%)`,
          tension: 0.3, pointRadius: 2
        });
      }
    });
    return { labels: [], datasets };
  }, [allData, startDate, endDate, timeRange, filterMode, targetMetric]);

  // 【1】「AI診断」ボタンを押したときの処理
  // いきなり通信せず、まずデバイス選択画面を開きます
  const handleOpenAiSelect = () => {
    if (fileList.length === 0) {
      alert("デバイスが見つかりません");
      return;
    }
    setIsAiSelectOpen(true); // 選択モーダルを開く
  };

  // 【2】デバイスを選択して診断を実行する処理
  // 選択画面でボタンを押すとここに来ます
  const executeAiDiagnosis = async (targetDevice) => {
    // UIの切り替え
    setIsAiSelectOpen(false); // 選択画面を閉じる
    setAiLoading(true);       // "診断中"画面を出す
    setDiagnosisResult(null); // 結果をリセット

    // キャンセル用のコントローラーを作成
    const controller = new AbortController();
    aiAbortController.current = controller;

    try {
      // API呼び出し (signalを渡すことでキャンセル可能にする)
      const res = await fetch(
        `${config.apiBase}/api/diagnose?device=${encodeURIComponent(targetDevice.rawName)}`, 
        { signal: controller.signal }
      );

      if (!res.ok) throw new Error("診断に失敗しました");

      const textResult = await res.text();

      // 結果をセットして結果画面を開く
      setDiagnosisResult({
        text: textResult,
        email: config.users[user]?.email || ""
      });
      setIsDiagnosisOpen(true); // 結果モーダルを開く

    } catch (error) {
      if (error.name === 'AbortError') {
        console.log("ユーザーにより診断がキャンセルされました");
        // キャンセルの場合は結果画面を開かない
      } else {
        console.error(error);
        alert("診断に失敗しました: " + error.message);
      }
    } finally {
      // 後始末
      setAiLoading(false);
      aiAbortController.current = null;
    }
  };

  // 【3】キャンセルボタンを押したときの処理
  const handleCancelAi = () => {
    if (aiAbortController.current) {
      aiAbortController.current.abort(); // 通信を強制切断
    }
  };

  // メールの手動送信
  const sendEmailReport = async () => {
    if (!diagnosisResult?.email) return alert("送信先メールアドレスが設定されていません");
    
    setLoading(true);
    try {
      await fetch(`${config.apiBase}/api/send-email`, {
        method: "POST",
        body: JSON.stringify({
          to_addresses: diagnosisResult.email,
          report_content: diagnosisResult.text
        })
      });
      alert("メールを送信しました");
    } catch (e) {
      alert("メール送信に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const currentUnit = targetMetric === 'ping' ? 'ms' : 'Mbps';
  const dynamicOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: { 
      legend: { labels: { color: '#ccc' } }, 
      tooltip: { 
        callbacks: {
          title: function(context) {
            const timestamp = context[0].parsed.x;
            return format(new Date(timestamp), 'yyyy/MM/dd HH:mm');
          },
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) { label += ': '; }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toFixed(2);
            }
            return label;
          }
        },
        mode: 'index', 
        intersect: false 
      } 
    },
    scales: {
      x: { 
        type: 'linear', min: startDate.getTime(), max: endDate.getTime(), 
        grid: { color: '#333' }, 
        ticks: { 
          color: '#888', maxRotation: 0, autoSkip: true, 
          // 期間が短い場合は目盛りを細かくするロジック
          stepSize: (endDate.getTime() - startDate.getTime()) / 6, 
          callback: (v) => { 
            const d = new Date(v); if(isNaN(d))return""; 
            return format(d, apiDuration === 'today' || apiDuration > 1440 ? 'MM/dd HH:mm' : 'HH:mm');
          } 
        } 
      },
      y: { grid: { color: '#333' }, ticks: { color: '#888' }, min: 0, title: { display: true, text: currentUnit, color: '#888' } }
    }
  };

  const midnightLinePlugin = { 
    id: 'midnightLine', 
    afterDraw: (chart) => { 
      const { ctx, scales: { x, y } } = chart; 
      const start = x.min; const end = x.max; 
      let d = new Date(start); d.setHours(0,0,0,0); 
      if(d.getTime()<start) d.setDate(d.getDate()+1); 
      ctx.save(); ctx.beginPath(); ctx.lineWidth = 1; ctx.strokeStyle = '#888'; 
      while(d.getTime()<=end){ 
        const xp = x.getPixelForValue(d.getTime()); 
        ctx.moveTo(xp, y.top); ctx.lineTo(xp, y.bottom); 
        d.setDate(d.getDate()+1); 
      } 
      ctx.stroke(); ctx.restore(); 
    } 
  };

  // --- 表示分岐 ---

  if (!config) {
    return <div style={{height:'100vh', display:'flex', justifyContent:'center', alignItems:'center', background:'#1e1e26', color:'white'}}>Loading Settings...</div>;
  }

  if (!user) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#1e1e26', color: 'white' }}>
        <form onSubmit={handleLogin} style={{ background: '#25252f', padding: '40px', borderRadius: '12px', width: '320px', textAlign: 'center', boxShadow: '0 4px 15px rgba(0,0,0,0.5)' }}>
          <h2 style={{ marginBottom: '25px', display:'flex', alignItems:'center', justifyContent:'center', gap:'10px' }}><Activity /> Monitor Login</h2>
          <div style={{position: 'relative', marginBottom: '15px'}}>
            <User size={18} style={{position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#888'}} />
            <input type="text" placeholder="Username" value={usernameInput} onChange={e => setUsernameInput(e.target.value)} style={{ width: '100%', padding: '10px 10px 10px 35px', borderRadius: '4px', border: '1px solid #444', background: '#1a1a1a', color: 'white', boxSizing: 'border-box' }} />
          </div>
          <div style={{position: 'relative', marginBottom: '25px'}}>
            <Lock size={18} style={{position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#888'}} />
            <input type="password" placeholder="Password" value={passwordInput} onChange={e => setPasswordInput(e.target.value)} style={{ width: '100%', padding: '10px 10px 10px 35px', borderRadius: '4px', border: '1px solid #444', background: '#1a1a1a', color: 'white', boxSizing: 'border-box' }} />
          </div>
          <button type="submit" style={{ width: '100%', padding: '12px', background: '#0066cc', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Login</button>
        </form>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', height: '100vh', backgroundColor: '#1e1e26', color: '#fff', fontFamily: 'sans-serif' }}>
      
      {/* サイドバー */}
      <div style={{ width: '300px', padding: '20px', borderRight: '1px solid #333', backgroundColor: '#25252f', display: 'flex', flexDirection: 'column' }}>
        <h3 style={{display:'flex', alignItems:'center', gap:'10px'}}><Activity size={20}/> Network Monitor</h3>
        <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: '5px' }}>Logged in as: <span style={{color:'#fff', fontWeight:'bold'}}>{user}</span></p>
        <p style={{ fontSize: '0.7rem', color: '#0066cc', marginBottom: '30px', textTransform: 'uppercase', border: '1px solid #0066cc', display:'inline-block', padding:'2px 6px', borderRadius:'4px' }}>
          {config.users[user]?.plan || 'economy'} plan
        </p>

        <div style={{ marginBottom: '25px' }}>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px'}}>
            <label style={{color:'#ccc'}}>デバイス選択</label>
            <button onClick={() => setIsSettingsOpen(true)} style={{background:'none', border:'none', color:'#0066cc', cursor:'pointer', fontSize:'12px', display:'flex', alignItems:'center', gap:'4px'}}>
              <Settings size={12}/> 管理
            </button>
          </div>
          <Select isMulti options={fileList} value={selectedDevices} onChange={setSelectedDevices} styles={customSelectStyles} />
        </div>

        <div style={{ marginBottom: '25px' }}>
          <label style={{display:'block', marginBottom:'8px', color:'#ccc'}}>表示データ</label>
          <div style={{ display:'flex', gap:'5px', background:'#1a1a1a', padding:'5px', borderRadius:'8px', flexWrap: 'wrap' }}>
            {[{ k: 'download', l: 'Down' }, { k: 'upload', l: 'Up' }, { k: 'both', l: 'Down&Up' }, { k: 'ping', l: 'Ping' }].map(i => (
              <button key={i.k} onClick={() => setTargetMetric(i.k)} style={{ flex: '1 0 40%', padding: '8px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', background: targetMetric === i.k ? '#0066cc' : 'transparent', color: targetMetric === i.k ? 'white' : '#888' }}>{i.l}</button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: '25px' }}>
          <label style={{display:'block', marginBottom:'8px', color:'#ccc'}}>表示モード</label>
          <div style={{ display:'flex', flexDirection:'column', gap:'10px', background:'#1a1a1a', padding:'10px', borderRadius:'8px' }}>
            <label style={{cursor:'pointer'}}><input type="radio" checked={displayMode === 'overlap'} onChange={() => setDisplayMode('overlap')} /> 重ねて表示</label>
            <label style={{cursor:'pointer'}}><input type="radio" checked={displayMode === 'separate'} onChange={() => setDisplayMode('separate')} /> 別々に表示</label>
          </div>
        </div>

        {/* CSVダウンロードボタン */}
        <div style={{ marginBottom: '25px', marginTop: '20px' }}>
          <button 
            onClick={() => {
              const csvRows = [["Device", "Date", "Ping", "Loss", "Download", "Upload"]];
              Object.entries(allData).forEach(([deviceName, rows]) => {
                rows.forEach(r => {
                  // D1形式から変換 [0]TS, [2]Ping, [3]Loss, [4]Down, [5]Up
                  const date = format(new Date(r[0]), 'yyyy/MM/dd HH:mm:ss');
                  csvRows.push([deviceName, date, r[2], r[3], r[4], r[5]]);
                });
              });

              if (csvRows.length <= 1) {
                alert("ダウンロードするデータがありません");
                return;
              }
              const csvContent = csvRows.map(e => e.join(",")).join("\n");
              const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
              const blob = new Blob([bom, csvContent], { type: 'text/csv;charset=utf-8;' });
              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.setAttribute("href", url);
              link.setAttribute("download", `network_report_${format(new Date(), 'yyyyMMdd_HHmm')}.csv`);
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            }}
            style={{
              width: '100%', padding: '12px', background: '#333', color: 'white', border: '1px solid #444', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontWeight: 'bold'
            }}
          >
            <Download size={18} /> CSVダウンロード
          </button>
        </div>

        <div style={{ marginTop: 'auto' }}>
          <button onClick={() => { setUser(null); setPasswordInput(""); }} className="sidebar-btn" style={{marginTop:'10px', backgroundColor:'#444'}}><LogOut size={16}/> ログアウト</button>
        </div>
      </div>

      {/* メインエリア */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#1a1a1a', overflowY: 'hidden' }}>
        <div style={headerContainerStyle}>
          <div style={headerLeftContainerStyle}>
            <h2>ネットワーク速度監視</h2>
            {/* はてなアイコンボタン */}
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <button onClick={() => setIsHelpOpen(!isHelpOpen)} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '5px' }}>
                <HelpCircle size={20} />
              </button>
              {/* 指標解説の吹き出し */}
              {isHelpOpen && (
                <div style={{
                  position: 'absolute', top: '35px', left: '50%', transform: 'translateX(-27px)', zIndex: 1000, width: '320px', background: '#25252f', border: '1px solid #444', borderRadius: '8px', padding: '15px', boxShadow: '0 10px 25px rgba(0,0,0,0.5)', fontSize: '0.85rem', lineHeight: '1.5', textAlign: 'left'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <span style={{ fontWeight: 'bold', color: '#55aaff' }}>指標の読み解き方</span>
                    <X size={14} style={{ cursor: 'pointer', color: '#888' }} onClick={() => setIsHelpOpen(false)} />
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <strong style={{ color: '#eee' }}>● ダウンロード (Down)</strong><br />
                    <span style={{ color: '#aaa' }}>契約している回線の<span style={{color: '#fff'}}>帯域幅</span>です。Webサイトの表示や動画視聴の快適さに直結します。</span>
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <strong style={{ color: '#eee' }}>● Ping</strong><br />
                    <span style={{ color: '#aaa' }}>回線の<span style={{color: '#fff'}}>応答品質</span>です。数値が低いほど「キレ」が良く、Web会議の遅延が少なくなります。</span>
                  </div>
                  <div>
                    <strong style={{ color: '#eee' }}>● アップロード (Up)</strong><br />
                    <span style={{ color: '#aaa' }}>クラウド利用の<span style={{color: '#fff'}}>実行速度</span>です。Zoomの映像送信やファイルのクラウド保存の速さです。</span>
                  </div>
                  <div style={{ position: 'absolute', top: '-6px', left: '20px', width: '10px', height: '10px', background: '#25252f', borderLeft: '1px solid #444', borderTop: '1px solid #444', transform: 'rotate(45deg)' }} />
                </div>
              )}
            </div>
          </div>
          <div style={headerRightContainerStyle}>
            <div style={{ position: 'relative' }}>
              <button onClick={() => setIsTimeMenuOpen(!isTimeMenuOpen)} style={headerBtnStyle}><Calendar size={16} /> <span>{quickLabel}</span><ChevronDown size={14} /></button>
              {isTimeMenuOpen && (
                <div style={megaMenuStyle}>
                  <div style={{ padding: '15px', borderRight: '1px solid #444', width: '300px' }}>
                    <h4 style={{ margin: '0 0 15px 0', color: '#ccc' }}>カスタム設定</h4>
                    <div style={{ marginBottom: '15px', display: 'flex', gap: '5px' }}>
                      <DatePicker selected={startDate} onChange={d => setStartDate(d)} dateFormat="yyyy/MM/dd" className="dark-input" />
                      <span style={{lineHeight:'34px'}}>-</span>
                      <DatePicker selected={endDate} onChange={d => setEndDate(d)} dateFormat="yyyy/MM/dd" className="dark-input" />
                    </div>
                    <div style={{ marginBottom: '20px' }}>
                      <Slider range min={0} max={1439} value={timeRange} onChange={setTimeRange} trackStyle={[{backgroundColor:'#ff4b4b'}]} handleStyle={[{borderColor:'#ff4b4b'},{borderColor:'#ff4b4b'}]} />
                    </div>
                    <button onClick={() => { setFilterMode('manual'); setQuickLabel('Custom'); setApiDuration('manual'); setIsTimeMenuOpen(false); }} style={applyBtnStyle}>適用する</button>
                  </div>
                  <div style={{ width: '180px' }}>
                    {quickRanges.map(r => (
                      <div key={r.label} onClick={() => { 
                        const now=new Date(); 
                        // UI表示用の範囲設定
                        if(r.value==='today'){ setStartDate(new Date(now.setHours(0,0,0,0))); setEndDate(new Date(now.setHours(23,59,59,999))); }
                        else { setEndDate(new Date()); setStartDate(new Date(Date.now() - r.value*60000)); }
                        
                        // ★重要: APIに送る期間(duration)を更新
                        setApiDuration(r.value);
                        
                        setFilterMode('quick'); setQuickLabel(r.label); setIsTimeMenuOpen(false);
                      }} style={quickItemStyle}>{r.label}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button onClick={fetchData} style={refreshBtnStyle}><RefreshCw size={16} className={loading ? 'spin' : ''} /></button>
            {config.users[user]?.plan !== 'economy' && (
              <button onClick={handleOpenAiSelect} className="ai-btn">
                <Brain size={18}/> AI診断
              </button>
            )}
          </div>
        </div>
        <div style={{ flex: 1, padding: '30px', overflowY: 'auto' }}>
          {loading ? <p style={{color:'#888', textAlign:'center', marginTop:'50px'}}>データを読み込んでいます...</p> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
               {displayMode === 'overlap' ? (
                 <div style={{ backgroundColor: '#25252f', padding: '20px', borderRadius: '12px', height: '500px' }}>
                   <Line data={filteredChartData} options={dynamicOptions} plugins={[midnightLinePlugin]} />
                 </div>
               ) : (
                 (() => {
                   const step = targetMetric === 'both' ? 2 : 1;
                   const charts = [];
                   for (let i = 0; i < filteredChartData.datasets.length; i += step) {
                     const ds = filteredChartData.datasets.slice(i, i + step);
                     let dn = ds[0].label.split('(')[0].trim();
                     charts.push(
                       <div key={i} style={{ backgroundColor: '#25252f', padding: '20px', borderRadius: '12px', height: '350px' }}>
                         <Line data={{ labels: [], datasets: ds }} options={{...dynamicOptions, plugins:{...dynamicOptions.plugins, title:{display:true, text:dn, color:'#fff', font:{size:16}}}}} plugins={[midnightLinePlugin]} />
                       </div>
                     );
                   }
                   return charts;
                 })()
               )}
            </div>
          )}
        </div>
      </div>

      {/* デバイス名設定モーダル */}
      {isSettingsOpen && (
        <div style={{ position: 'fixed', top:0, left:0, width:'100%', height:'100%', background:'rgba(0,0,0,0.7)', display:'flex', justifyContent:'center', alignItems:'center', zIndex:2000 }}>
          <div style={{ background: '#25252f', padding: '30px', borderRadius: '12px', width: '500px', maxHeight: '80%', overflowY: 'auto' }}>
            <h3 style={{ marginTop: 0 }}>デバイス名管理</h3>
            <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: '20px' }}>表示名を変更できます（サーバーに保存され、全員に共有されます）</p>
            {fileList.map((file) => (
              <div key={file.value} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center' }}>
                <div style={{ fontSize: '0.8rem', color: '#fff', width: '165px', overflow:'hidden', textOverflow:'ellipsis' }}>{file.rawName}</div>
                <input 
                  type="text" 
                  defaultValue={file.label}
                  placeholder="表示名を入力"
                  onBlur={(e) => handleRename(file.rawName, e.target.value)}
                  style={{ flex: 1, padding: '8px', background: '#1a1a1a', border: '1px solid #444', color: 'white', borderRadius: '4px' }}
                />
                <Edit2 size={14} color="#666"/>
              </div>
            ))}
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setIsSettingsOpen(false)} style={{ padding: '10px 20px', background: '#0066cc', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>閉じる</button>
            </div>
          </div>
        </div>
      )}

      {/* AI診断モーダル */}
      {isDiagnosisOpen && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            {isDiagnosing ? (
              <div style={loadingAreaStyle}><Brain className="spin" /> 解析中...</div>
            ) : (
              <div style={{ textAlign: 'left' }}>
                <h3>AIネットワーク診断結果</h3>
                <div style={reportBoxStyle}>{diagnosisResult?.text}</div>
                
                <div style={buttonGroupStyle}>
                  <button onClick={() => setIsDiagnosisOpen(false)} style={cancelBtnStyle}>閉じる</button>
                  {diagnosisResult?.email && (
                    <button onClick={sendEmailReport} style={sendBtnStyle}>
                      <Mail size={16}/> {diagnosisResult.email} へ送信
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* --- A. デバイス選択モーダル --- */}
      {isAiSelectOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 2000,
          display: 'flex', justifyContent: 'center', alignItems: 'center'
        }}>
          <div style={{
            backgroundColor: '#1a1a2e', padding: '24px', borderRadius: '12px',
            width: '90%', maxWidth: '400px', border: '1px solid #444', color: '#fff'
          }}>
            <h3 style={{marginBottom: '15px', borderBottom:'1px solid #444', paddingBottom:'10px'}}>
              診断するデバイスを選択
            </h3>
            <div style={{display: 'flex', flexDirection: 'column', gap: '8px', maxHeight:'300px', overflowY:'auto'}}>
              {fileList.map((dev) => (
                <button
                  key={dev.value}
                  // クリックすると診断開始関数【2】を呼ぶ
                  onClick={() => executeAiDiagnosis({ rawName: dev.value, label: dev.label })}
                  style={{
                    padding: '12px', borderRadius: '6px', border: '1px solid #555',
                    background: '#2a2a3e', color: '#eee', cursor: 'pointer', textAlign: 'left'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = '#3a3a4e'}
                  onMouseOut={(e) => e.currentTarget.style.background = '#2a2a3e'}
                >
                  {dev.label}
                </button>
              ))}
            </div>
            <button 
              onClick={() => setIsAiSelectOpen(false)}
              style={{marginTop: '20px', width: '100%', padding: '10px', background: '#555', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer'}}
            >
              閉じる
            </button>
          </div>
        </div>
      )}

      {/* --- B. 診断中ローディング（キャンセル付き） --- */}
      {aiLoading && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 2100,
          display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column'
        }}>
          <div style={{color: '#fff', fontSize: '18px', marginBottom: '20px', fontWeight: 'bold'}}>
            AIが診断レポートを作成中...
          </div>
          {/* ローディングアニメーション */}
          <div style={{
            marginBottom: '30px', width: '50px', height: '50px', 
            border: '5px solid #333', borderTop: '5px solid #00d4ff', borderRadius: '50%', 
            animation: 'spin 1s linear infinite'
          }}>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          </div>

          <button 
            // クリックするとキャンセル関数【3】を呼ぶ
            onClick={handleCancelAi}
            style={{
              padding: '10px 30px', background: '#ff4444', color: '#fff', 
              border: 'none', borderRadius: '25px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px'
            }}
          >
            キャンセル
          </button>
        </div>
      )}

      <style>{`
        .dark-input { background: #1a1a1a; color: white; border: 1px solid #555; padding: 8px; width: 110px; border-radius: 4px; text-align: center; }
        .sidebar-btn { width: 100%; padding: 12px; border: none; background: transparent; color: #ccc; border-radius: 6px; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; transition: 0.2s; }
        .sidebar-btn:hover { background: #333; color: white; }
        .ai-btn { background: #ff4b4b; color: white; border: none; padding: 8px 16px; border-radius: 6px; cursor: pointer; display: flex; align-items: center; gap: 6px; font-weight: bold; }
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

// Style定数はそのまま
const headerContainerStyle = { display: 'flex', justifyContent: 'space-between', marginBottom: '20px', borderBottom: '1px solid rgb(51, 51, 51)', padding: '10px 30px' };
const headerLeftContainerStyle = { display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '10px', backgroundColor: '#1a1a1a' };
const headerRightContainerStyle = { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px',  backgroundColor: '#1a1a1a' };
const headerBtnStyle = { display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#25252f', color: '#ccc', border: '1px solid #444', padding: '8px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', minWidth: '160px', justifyContent: 'space-between' };
const refreshBtnStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0066cc', color: '#fff', border: 'none', padding: '8px', borderRadius: '4px', cursor: 'pointer', width: '36px', height: '36px' };
const applyBtnStyle = { width: '100%', padding: '10px', backgroundColor: '#0066cc', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginTop: '10px' };
const megaMenuStyle = { position: 'absolute', top: '110%', right: 0, display: 'flex', backgroundColor: '#25252f', border: '1px solid #444', boxShadow: '0 10px 25px rgba(0,0,0,0.5)', zIndex: 1000, borderRadius: '6px', overflow: 'hidden' };
const quickItemStyle = { padding: '10px 15px', cursor: 'pointer', fontSize: '13px', color: '#ccc', borderBottom: '1px solid #333', transition: '0.2s' };
const customSelectStyles = { control: (base) => ({ ...base, background: '#1a1a1a', borderColor: '#444' }), menu: (base) => ({ ...base, background: '#1a1a1a' }), option: (base, state) => ({ ...base, background: state.isFocused ? '#333' : '#1a1a1a', color: 'white' }), multiValue: (base) => ({ ...base, background: '#0066cc' }), multiValueLabel: (base) => ({ ...base, color: 'white' }) };
const modalOverlayStyle = { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0, 0, 0, 0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2100 };
const modalContentStyle = { background: '#25252f', padding: '40px', borderRadius: '12px', width: '650px', maxHeight: '85vh', overflowY: 'auto', border: '1px solid #444', boxShadow: '0 0 30px rgba(0,0,0,0.5)' };
const loadingAreaStyle = { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '250px', gap: '20px', fontSize: '1.2rem' };
const reportBoxStyle = { background: '#1a1a1a', padding: '25px', borderRadius: '8px', marginBottom: '25px', whiteSpace: 'pre-wrap', lineHeight: '1.8', fontSize: '14px', color: '#eee', border: '1px solid #333' };
const buttonGroupStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', gap: '15px' };
const cancelBtnStyle = { padding: '12px 25px', background: 'transparent', color: '#ccc', border: '1px solid #555', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' };
const sendBtnStyle = { padding: '12px 25px', background: '#00cc66', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' };

export default App;